// aiConversationService.js

import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { CHANNEL_TYPES } from 'channels/constants/channelTypes';
import {
  AI_CONVERSATION_KIND,
  AI_LEGACY_FEED_TITLES,
  AI_NEW_CHAT_TITLE,
  AI_SESSION_KINDS,
  AI_TITLE_SOURCES,
  AI_UPDATES_TITLE,
} from 'spaces/constants/aiConstants';
import {
  buildAiChatChannelKey,
  buildAiFeedChannelKey,
  buildAiMemberKeysHash,
  isAiChatSession,
  isAiFeedSession,
} from 'spaces/utils/aiChannelKeys';
import { generateKey } from 'utilities/Keys';
import { collections, db, functions } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const channelDocRef = (baseKey, channelKey) => (
  doc(db, collections.bases, baseKey, 'channels', channelKey)
);

const ensureAiChannelCallable = httpsCallable(functions, 'spacesEnsureAiChannel');
const createAiChatSessionCallable = httpsCallable(
  functions,
  'spacesCreateAiChatSession',
);
const deleteAiChatSessionCallable = httpsCallable(
  functions,
  'spacesDeleteAiChatSession',
);

const inflightEnsures = new Map();
const recentFailures = new Map();
const FAILURE_COOLDOWN_MS = 60_000;

function buildEnsureCacheKey(baseKey, userKey) {
  return `${baseKey}:${userKey}`;
}

function mapChannelSnap(docSnap) {
  if (!docSnap?.exists?.()) return null;
  return {
    id: docSnap.id,
    key: docSnap.id,
    ...(docSnap.data() || {}),
  };
}

function cleanString(value) {
  return String(value || '').trim();
}

async function ensureAiChannelViaCallable({
  baseKey,
  userName,
}) {
  const response = await ensureAiChannelCallable({
    ...buildTenantCallableFields(baseKey),
    userName,
  });

  const channelKey = cleanString(response?.data?.channelKey);
  if (!channelKey) {
    throw new Error('Failed to provision Ai channel.');
  }

  const createdSnap = await getDoc(channelDocRef(baseKey, channelKey));
  const channel = mapChannelSnap(createdSnap);

  if (!channel) {
    throw new Error('Ai channel was provisioned but could not be loaded.');
  }

  return {
    channel,
    created: response?.data?.created === true,
  };
}

async function ensureAiChannelInternal({
  baseKey,
  userKey,
  userName = 'Member',
} = {}) {
  const cleanBaseKey = cleanString(baseKey);
  const cleanUserKey = cleanString(userKey);

  if (!cleanBaseKey || !cleanUserKey) {
    return { channel: null, created: false };
  }

  const cacheKey = buildEnsureCacheKey(cleanBaseKey, cleanUserKey);
  const channelKey = buildAiFeedChannelKey(cleanBaseKey, cleanUserKey);
  const ref = channelDocRef(cleanBaseKey, channelKey);
  const existingSnap = await getDoc(ref);
  const existingChannel = mapChannelSnap(existingSnap);

  if (existingChannel) {
    recentFailures.delete(cacheKey);
    const titleLower = cleanString(existingChannel.title).toLowerCase();
    const titleSource = cleanString(existingChannel.titleSource);
    const shouldRewriteTitle = titleSource !== AI_TITLE_SOURCES.USER
      && (
        !titleLower
        || AI_LEGACY_FEED_TITLES.includes(titleLower)
      );
    const needsBackfill = cleanString(existingChannel.sessionKind) !== AI_SESSION_KINDS.FEED
      || !titleSource
      || shouldRewriteTitle;
    if (needsBackfill) {
      try {
        await updateDoc(ref, {
          sessionKind: AI_SESSION_KINDS.FEED,
          ...(shouldRewriteTitle
            ? {
              title: AI_UPDATES_TITLE,
              titleLower: AI_UPDATES_TITLE.toLowerCase(),
              titleSource: AI_TITLE_SOURCES.SYSTEM,
            }
            : (!titleSource
              ? { titleSource: AI_TITLE_SOURCES.SYSTEM }
              : {})),
          dateModified: serverTimestamp(),
        });
        const refreshed = mapChannelSnap(await getDoc(ref));
        return { channel: refreshed || existingChannel, created: false };
      } catch {
        return { channel: existingChannel, created: false };
      }
    }
    return { channel: existingChannel, created: false };
  }

  const now = serverTimestamp();
  const payload = {
    key: channelKey,
    baseKey: cleanBaseKey,
    channelType: CHANNEL_TYPES.CONVERSATION,
    conversationKind: AI_CONVERSATION_KIND,
    sessionKind: AI_SESSION_KINDS.FEED,
    titleSource: AI_TITLE_SOURCES.SYSTEM,
    dmMemberKeysHash: buildAiMemberKeysHash(cleanUserKey),
    memberKeys: [cleanUserKey],
    moduleKey: null,
    title: AI_UPDATES_TITLE,
    titleLower: AI_UPDATES_TITLE.toLowerCase(),
    topic: '',
    createdBy: cleanUserKey,
    createdByName: userName,
    messagingEnabled: true,
    messagingDisabledReason: null,
    inactiveMemberKeys: [],
    pinned: true,
    pinnedAt: now,
    dateCreated: now,
    dateModified: now,
    lastMessageAt: null,
    lastMessageByKey: null,
    lastMessageByName: null,
    lastMessagePreview: '',
  };

  try {
    await setDoc(ref, payload, { merge: false });
    recentFailures.delete(cacheKey);
    return { channel: payload, created: true };
  } catch (error) {
    const isPermissionDenied = String(error?.code || '').includes('permission-denied');

    if (isPermissionDenied) {
      const viaCallable = await ensureAiChannelViaCallable({
        baseKey: cleanBaseKey,
        userName,
      });
      recentFailures.delete(cacheKey);
      return viaCallable;
    }

    const retrySnap = await getDoc(ref);
    const retryChannel = mapChannelSnap(retrySnap);
    if (retryChannel) {
      recentFailures.delete(cacheKey);
      return { channel: retryChannel, created: false };
    }

    throw error;
  }
}

/** Ensure the durable Feed session (legacy Ai channel). */
export async function ensureAiFeedSession(options = {}) {
  return ensureAiChannel(options);
}

export async function ensureAiChannel({
  baseKey,
  userKey,
  userName = 'Member',
  force = false,
} = {}) {
  const cleanBaseKey = cleanString(baseKey);
  const cleanUserKey = cleanString(userKey);
  const cacheKey = buildEnsureCacheKey(cleanBaseKey, cleanUserKey);

  if (!cleanBaseKey || !cleanUserKey) {
    return { channel: null, created: false };
  }

  if (!force) {
    const failedAt = recentFailures.get(cacheKey);
    if (failedAt && (Date.now() - failedAt) < FAILURE_COOLDOWN_MS) {
      const existingSnap = await getDoc(channelDocRef(
        cleanBaseKey,
        buildAiFeedChannelKey(cleanBaseKey, cleanUserKey),
      ));
      const existingChannel = mapChannelSnap(existingSnap);
      if (existingChannel) {
        recentFailures.delete(cacheKey);
        return { channel: existingChannel, created: false };
      }
      return { channel: null, created: false, skipped: true };
    }
  }

  if (inflightEnsures.has(cacheKey)) {
    return inflightEnsures.get(cacheKey);
  }

  const promise = ensureAiChannelInternal({
    baseKey: cleanBaseKey,
    userKey: cleanUserKey,
    userName,
  }).catch((error) => {
    recentFailures.set(cacheKey, Date.now());
    throw error;
  }).finally(() => {
    inflightEnsures.delete(cacheKey);
  });

  inflightEnsures.set(cacheKey, promise);
  return promise;
}

async function createAiChatSessionViaCallable({
  baseKey,
  userName,
}) {
  const response = await createAiChatSessionCallable({
    ...buildTenantCallableFields(baseKey),
    userName,
  });

  const channelKey = cleanString(response?.data?.channelKey);
  if (!channelKey) {
    throw new Error('Failed to create Ai chat session.');
  }

  const createdSnap = await getDoc(channelDocRef(baseKey, channelKey));
  const channel = mapChannelSnap(createdSnap);

  if (!channel) {
    throw new Error('Ai chat session was created but could not be loaded.');
  }

  return {
    channel,
    created: response?.data?.created === true,
  };
}

export async function createAiChatSession({
  baseKey,
  userKey,
  userName = 'Member',
} = {}) {
  const cleanBaseKey = cleanString(baseKey);
  const cleanUserKey = cleanString(userKey);

  if (!cleanBaseKey || !cleanUserKey) {
    return { channel: null, created: false };
  }

  const sessionId = generateKey(20);
  const channelKey = buildAiChatChannelKey(
    cleanBaseKey,
    cleanUserKey,
    sessionId,
  );
  const ref = channelDocRef(cleanBaseKey, channelKey);
  const now = serverTimestamp();
  const payload = {
    key: channelKey,
    baseKey: cleanBaseKey,
    channelType: CHANNEL_TYPES.CONVERSATION,
    conversationKind: AI_CONVERSATION_KIND,
    sessionKind: AI_SESSION_KINDS.CHAT,
    titleSource: AI_TITLE_SOURCES.SYSTEM,
    dmMemberKeysHash: buildAiMemberKeysHash(cleanUserKey),
    memberKeys: [cleanUserKey],
    moduleKey: null,
    title: AI_NEW_CHAT_TITLE,
    titleLower: AI_NEW_CHAT_TITLE.toLowerCase(),
    topic: '',
    createdBy: cleanUserKey,
    createdByName: userName,
    messagingEnabled: true,
    messagingDisabledReason: null,
    inactiveMemberKeys: [],
    pinned: false,
    pinnedAt: null,
    dateCreated: now,
    dateModified: now,
    lastMessageAt: null,
    lastMessageByKey: null,
    lastMessageByName: null,
    lastMessagePreview: '',
  };

  try {
    await setDoc(ref, payload, { merge: false });
    return { channel: { ...payload, id: channelKey, key: channelKey }, created: true };
  } catch (error) {
    const isPermissionDenied = String(error?.code || '').includes('permission-denied');
    if (isPermissionDenied) {
      return createAiChatSessionViaCallable({
        baseKey: cleanBaseKey,
        userName,
      });
    }
    throw error;
  }
}

export async function updateAiSessionMeta({
  baseKey,
  channelKey,
  title,
  pinned,
  titleSource,
  userKey,
} = {}) {
  const cleanBaseKey = cleanString(baseKey);
  const cleanChannelKey = cleanString(channelKey);
  const cleanUserKey = cleanString(userKey);
  if (!cleanBaseKey || !cleanChannelKey) {
    throw new Error('Base and session are required.');
  }

  const updates = {
    dateModified: serverTimestamp(),
  };

  if (title !== undefined) {
    const nextTitle = cleanString(title) || AI_NEW_CHAT_TITLE;
    updates.title = nextTitle;
    updates.titleLower = nextTitle.toLowerCase();
    updates.titleSource = cleanString(titleSource) || AI_TITLE_SOURCES.USER;
  }

  if (pinned !== undefined) {
    updates.pinned = pinned === true;
    updates.pinnedAt = pinned === true ? serverTimestamp() : null;
  }

  // Heal legacy chat sessions missing sessionKind so delete rules stay aligned.
  if (cleanUserKey) {
    try {
      const snap = await getDoc(channelDocRef(cleanBaseKey, cleanChannelKey));
      const channel = mapChannelSnap(snap);
      if (
        channel
        && !cleanString(channel.sessionKind)
        && isAiChatSession(channel, cleanBaseKey, cleanUserKey)
      ) {
        updates.sessionKind = AI_SESSION_KINDS.CHAT;
      }
    } catch (_error) {
      // Best-effort backfill only; rename/pin should still proceed.
    }
  }

  await updateDoc(channelDocRef(cleanBaseKey, cleanChannelKey), updates);
}

async function deleteAiChatSessionViaCallable({
  baseKey,
  channelKey,
}) {
  const response = await deleteAiChatSessionCallable({
    ...buildTenantCallableFields(baseKey),
    channelKey,
  });
  return {
    deleted: response?.data?.deleted === true,
    channelKey: cleanString(response?.data?.channelKey) || cleanString(channelKey),
  };
}

export async function deleteAiChatSession({
  baseKey,
  channelKey,
  userKey,
} = {}) {
  const cleanBaseKey = cleanString(baseKey);
  const cleanChannelKey = cleanString(channelKey);
  const cleanUserKey = cleanString(userKey);
  if (!cleanBaseKey || !cleanChannelKey) {
    throw new Error('Base and session are required.');
  }

  try {
    const snap = await getDoc(channelDocRef(cleanBaseKey, cleanChannelKey));
    const channel = mapChannelSnap(snap);
    if (!channel) return;

    if (isAiFeedSession(channel, cleanBaseKey, cleanUserKey)) {
      throw new Error('The Ai feed session cannot be deleted.');
    }

    await deleteDoc(channelDocRef(cleanBaseKey, cleanChannelKey));
  } catch (error) {
    const code = String(error?.code || '');
    const message = String(error?.message || '');
    const isFeedGuard = message.includes('feed session cannot be deleted');
    if (isFeedGuard) throw error;

    const isPermissionDenied = code.includes('permission-denied');
    if (!isPermissionDenied) throw error;

    await deleteAiChatSessionViaCallable({
      baseKey: cleanBaseKey,
      channelKey: cleanChannelKey,
    });
  }
}

/** Aliases for shorter call-site names. */
export const createAiSession = createAiChatSession;
export const deleteAiSession = deleteAiChatSession;
